// Blockade Labs Skybox AI — generate 360° equirect skyboxes from a prompt.
// Docs: https://api-documentation.blockadelabs.com
// Always calls the same-origin Cloudflare Pages Function (api/skybox/*) so the
// API key never ships in the client (dev: Vite proxies /api to the live site).
//
// In production a generation costs Studio credits (Standard 1, Enhanced 2),
// charged server-side by the proxy; see functions/api/skybox/[[path]].ts.

import { supabase } from '../config/supabase';
import { gathrxrAuthHeader } from '../utils/quotaHeaders';

// Always the same-origin Pages Function: the key lives server-side only. In
// dev, Vite proxies /api to the live functions (see vite.config.ts), so no
// provider key is ever read by client code (and none can be inlined).
const BASE = '/api/skybox';

function headers(): Record<string, string> {
  return { 'Content-Type': 'application/json' };
}

export interface SkyboxStyleVariant {
  id: number;
  key: 'standard' | 'enhanced' | string;
  label?: string;
  credit_cost?: number;
  is_default?: boolean;
}

export interface SkyboxStyle {
  id: number;
  name: string;
  description?: string;
  image_jpg?: string;
  model?: string;             // 'Model 2' | 'Model 3' | 'Model 4'
  sort_order?: number;
  'max-char'?: number;
  'negative-text-max-char'?: number;
  variants?: SkyboxStyleVariant[];
}

export interface SkyboxGeneration {
  id: number;
  status: 'pending' | 'dispatched' | 'processing' | 'complete' | 'abort' | 'error';
  file_url?: string;      // equirect image when complete
  thumb_url?: string;
  error_message?: string;
}

export type SkyboxQuality = 'standard' | 'enhanced';

/** Studio credits per Skybox AI generation (server prices it the same way). */
export const SKYBOX_CREDITS: Record<SkyboxQuality, number> = { standard: 1, enhanced: 2 };

/** Blockade variant ids for Model 4 styles. */
const VARIANT_ID: Record<SkyboxQuality, number> = { standard: 1, enhanced: 2 };

/** A refusal the UI can act on: out of credits (offer the Studio pack) or the monthly cap. */
export class SkyboxError extends Error {
  needsCredits: boolean;
  capReached: boolean;
  status: number;
  constructor(message: string, status: number, flags: { needsCredits?: boolean; capReached?: boolean } = {}) {
    super(message);
    this.status = status;
    this.needsCredits = !!flags.needsCredits;
    this.capReached = !!flags.capReached;
  }
}

let stylesPromise: Promise<SkyboxStyle[]> | null = null;

/** All Blockade styles (cached for the session). */
export function fetchSkyboxStyles(): Promise<SkyboxStyle[]> {
  if (!stylesPromise) {
    stylesPromise = (async () => {
      const res = await fetch(`${BASE}/skybox/styles`, { headers: headers() });
      if (!res.ok) throw new Error(`Failed to load skybox styles: ${res.status}`);
      return res.json();
    })();
    stylesPromise.catch(() => { stylesPromise = null; });
  }
  return stylesPromise;
}

export interface GenerateSkyboxOptions {
  quality?: SkyboxQuality;
  /** A finished Studio world job: its one sky is already paid for. */
  studioJobId?: string;
}

export async function generateSkybox(prompt: string, styleId?: number, negativeText?: string, opts: GenerateSkyboxOptions = {}): Promise<number> {
  const res = await fetch(`${BASE}/skybox`, {
    method: 'POST',
    headers: { ...headers(), ...(await gathrxrAuthHeader()) },
    body: JSON.stringify({
      prompt,
      ...(styleId ? { skybox_style_id: styleId } : {}),
      ...(styleId && opts.quality ? { variant_id: VARIANT_ID[opts.quality] } : {}),
      ...(negativeText ? { negative_text: negativeText } : {}),
      // Proxy-only field (stripped before Blockade).
      ...(opts.studioJobId ? { gathrxr_studio_job: opts.studioJobId } : {}),
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data?.error?.message || data?.message || (typeof data?.error === 'string' ? data.error : '') || `Skybox generation failed: ${res.status}`;
    throw new SkyboxError(msg, res.status, { needsCredits: !!data?.needsCredits, capReached: !!data?.capReached });
  }
  return data.id;
}

/** The signed-in user's Studio credits (Skybox AI spends these). null = signed out. */
export async function getSkyboxCredits(): Promise<{ admin: boolean; remaining: number; packUnits: number } | null> {
  try {
    const { data, error } = await supabase.rpc('studio_access');
    if (error || !data?.signed_in) return null;
    return { admin: !!data.admin, remaining: data.admin ? Infinity : Number(data.remaining ?? 0), packUnits: Number(data.pack_units ?? 6) };
  } catch {
    return null;
  }
}

export async function getSkyboxStatus(id: number): Promise<SkyboxGeneration> {
  const res = await fetch(`${BASE}/imagine/requests/${id}`, { headers: headers() });
  if (!res.ok) throw new Error(`Skybox status failed: ${res.status}`);
  const data = await res.json();
  return data.request || data;
}

/** Generate and poll until complete; returns the equirect image URL. */
export async function generateSkyboxAndWait(
  prompt: string,
  styleId: number | undefined,
  onProgress?: (status: string) => void,
  negativeText?: string,
  opts: GenerateSkyboxOptions = {},
): Promise<{ fileUrl: string; thumbUrl?: string }> {
  const id = await generateSkybox(prompt, styleId, negativeText, opts);
  for (let i = 0; i < 90; i++) {
    const st = await getSkyboxStatus(id);
    onProgress?.(st.status);
    if (st.status === 'complete' && st.file_url) return { fileUrl: st.file_url, thumbUrl: st.thumb_url };
    if (st.status === 'abort' || st.status === 'error') {
      throw new Error(st.error_message || 'Skybox generation failed');
    }
    await new Promise((r) => setTimeout(r, 4000));
  }
  throw new Error('Skybox generation timed out');
}
